import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Textarea } from './textarea';
import { Button } from './button';
import { Badge } from './badge';
import { toast } from 'sonner';
import { SaveIcon, CheckIcon, RefreshIcon, HistoryIcon } from './icons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Section-specific placeholders with guidance
const SECTION_PLACEHOLDERS = {
  belonging: `Write about your community's foundation of presence...

Consider these elements:
• Welcome rituals & arrival practices
• Physical space design for safety
• Recognition & visibility protocols
• Story-sharing ceremonies
• Integration pathways for newcomers

How will people know they truly belong here?`,
  governance: `Describe your council circle and stewardship structures...

Consider these elements:
• Decision-making frameworks
• Council roles & responsibilities
• Consent vs consensus protocols
• Meeting rhythms & sacred times
• Documentation & transparency

How will collective wisdom guide your community?`,
  boundaries: `Define your sacred threshold...

Consider these elements:
• Shared values & alignment criteria
• Initiation & onboarding journey
• Membership agreements & commitments
• Graceful exit pathways
• Boundary restoration protocols

Who belongs, and how do they join?`,
  labor: `Outline your commons of contribution...

Consider these elements:
• Work distribution agreements
• Skill-sharing & mentorship
• Rest & renewal practices
• Recognition of invisible labor
• Economic sustainability models

How is work shared fairly and sustainably?`,
  conflict: `Describe your alchemy of tension...

Consider these elements:
• Early intervention practices
• Restorative dialogue protocols
• Elder & witness roles
• Accountability without shame
• Repair & reconciliation rituals

How will discord become deeper connection?`,
  leadership: `Define the way of stewardship...

Consider these elements:
• Role clarity & boundaries
• Selection & rotation practices
• Accountability structures
• Mentorship & succession
• Preventing power concentration

Who guides, and how are they held accountable?`,
  continuity: `Envision the long arc...

Consider these elements:
• Multi-generational planning
• Land & resource stewardship
• Cultural preservation practices
• Story & lore keeping
• Adaptation & evolution protocols

What legacy will you leave?`,
  interdependence: `Describe the weaving...

Consider these elements:
• Individual sovereignty agreements
• Collective care practices
• Resource sharing protocols
• External relationship guidelines
• Network of communities vision

How do self and collective dance together?`
};

export const CollaborativeCanvas = ({
  projectId,
  section,
  userName,
  onContentChange,
  externalContent,
  externalUpdatedBy,
  insertedContent,
  onInsertComplete,
}) => {
  const [content, setContent] = useState('');
  const [saveStatus, setSaveStatus] = useState('saved');
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const saveTimeoutRef = useRef(null);
  const lastSavedContent = useRef('');
  const textareaRef = useRef(null);

  // Load initial content
  useEffect(() => {
    if (section?.content !== undefined) {
      setContent(section.content);
      lastSavedContent.current = section.content;
      setVersions(section.versions || []);
    }
  }, [section]);

  // Handle external content updates (from WebSocket)
  useEffect(() => {
    if (externalContent !== undefined && externalUpdatedBy !== userName) {
      setContent(externalContent);
      lastSavedContent.current = externalContent;
      setSaveStatus('saved');
    }
  }, [externalContent, externalUpdatedBy, userName]);

  // Handle inserted content from M.ai.ry
  useEffect(() => {
    if (insertedContent) {
      const newContent = content 
        ? `${content}\n\n${insertedContent}`
        : insertedContent;
      setContent(newContent);
      setSaveStatus('unsaved');
      
      // Trigger save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveContent(newContent);
      }, 500);
      
      // Scroll to bottom of textarea
      if (textareaRef.current) {
        setTimeout(() => {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }, 100);
      }
      
      if (onInsertComplete) {
        onInsertComplete();
      }
    }
  }, [insertedContent]);

  // Autosave function
  const saveContent = useCallback(async (contentToSave) => {
    if (contentToSave === lastSavedContent.current) {
      setSaveStatus('saved');
      return;
    }

    setSaveStatus('saving');

    try {
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/sections/${section.key}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: contentToSave,
            updated_by: userName,
          }),
        }
      );

      if (!response.ok) throw new Error('Failed to save');

      lastSavedContent.current = contentToSave;
      setSaveStatus('saved');
      onContentChange?.(contentToSave);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('unsaved');
      toast.error('Failed to save. Please try again.');
    }
  }, [projectId, section?.key, userName, onContentChange]);

  // Handle content change with debounced autosave
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    setSaveStatus('unsaved');

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveContent(newContent);
    }, 800);
  };

  // Save version snapshot
  const saveVersion = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/sections/${section.key}/version`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: content,
            updated_by: userName,
          }),
        }
      );

      if (!response.ok) throw new Error('Failed to save version');

      const data = await response.json();
      setVersions(data.versions || []);
      toast.success('Version saved!');
    } catch (error) {
      console.error('Version save error:', error);
      toast.error('Failed to save version');
    }
  };

  // Restore version
  const restoreVersion = (version) => {
    setContent(version.content);
    setSaveStatus('unsaved');
    setShowVersions(false);
    saveContent(version.content);
    toast.success('Version restored!');
  };

  // Calculate word count
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (!section) return null;

  // Get section meta for display
  const SECTION_TITLES = {
    belonging: "The Foundation of Presence",
    governance: "The Council Circle",
    boundaries: "The Sacred Threshold",
    labor: "The Commons of Contribution",
    conflict: "The Alchemy of Tension",
    leadership: "The Way of Stewardship",
    continuity: "The Long Arc",
    interdependence: "The Weaving"
  };

  const SECTION_DESCRIPTIONS = {
    belonging: "Physiological safety & the roots of true connection",
    governance: "Stewardship structures & collective wisdom",
    boundaries: "Membership, entry, and honorable departure",
    labor: "Shared work, fair exchange & sustainable effort",
    conflict: "Transforming discord into deeper connection",
    leadership: "Roles, accountability & servant guidance",
    continuity: "Legacy, land stewardship & generational vision",
    interdependence: "Balance of self & collective, unity in diversity"
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-[var(--fg)]">
            {SECTION_TITLES[section.key] || section.title}
          </h2>
          <p className="text-sm text-[var(--fg)]/60 mt-1">
            {SECTION_DESCRIPTIONS[section.key] || section.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-1.5"
            data-testid="canvas-saving-indicator"
          >
            {saveStatus === 'saving' && (
              <>
                <RefreshIcon size={16} className="text-[var(--accent)] animate-spin" />
                <span className="text-xs text-[var(--fg)]/60">Saving...</span>
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <CheckIcon size={16} className="text-[var(--success)]" />
                <span className="text-xs text-[var(--fg)]/60">Saved</span>
              </>
            )}
            {saveStatus === 'unsaved' && (
              <>
                <SaveIcon size={16} className="text-[var(--warning)]" />
                <span className="text-xs text-[var(--fg)]/60">Unsaved</span>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 relative">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          placeholder={SECTION_PLACEHOLDERS[section.key] || `Write your thoughts on ${section.title.toLowerCase()}...`}
          className="h-full min-h-[300px] resize-none text-base leading-relaxed"
          data-testid="canvas-textarea"
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--muted)]/50">
        <div className="flex items-center gap-4">
          <span className="text-xs text-[var(--fg)]/50">{wordCount} words</span>
          {section.updated_by && (
            <span className="text-xs text-[var(--fg)]/50">
              Last edited by {section.updated_by}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowVersions(!showVersions)}
            className="gap-1.5"
            data-testid="canvas-versions-button"
          >
            <HistoryIcon size={16} />
            Versions ({versions.length})
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={saveVersion}
            className="gap-1.5"
            data-testid="canvas-save-version-button"
          >
            <SaveIcon size={16} />
            Save Snapshot
          </Button>
        </div>
      </div>

      {/* Versions panel */}
      {showVersions && versions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-4 p-4 bg-[var(--muted)]/30 rounded-[var(--radius-sm)] border border-[var(--muted)]"
        >
          <h4 className="text-sm font-medium mb-3">Version History</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {versions.slice().reverse().map((version, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-2 bg-white rounded-[var(--radius-xs)] border border-[var(--muted)]/50"
              >
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="text-xs">
                    v{versions.length - i}
                  </Badge>
                  <span className="text-xs text-[var(--fg)]/60">
                    {new Date(version.saved_at).toLocaleString()}
                  </span>
                  <span className="text-xs text-[var(--fg)]/40">
                    by {version.saved_by}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => restoreVersion(version)}
                  className="text-xs h-7"
                >
                  Restore
                </Button>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};
